import axios from 'axios'
import React, {useEffect, useState} from 'react'
import {useHistory, useParams} from 'react-router-dom'
import {EDIT_RECORD_INPUTS} from '../../../Notes/Nurses_Notes/GENERAL_CONSTANTS'
import DynamicForm from '../../../../reusable-components/forms/DynamicForm'
import {toast} from 'react-toastify'
import dayjs from 'dayjs'
import {LocalizationProvider} from '@mui/x-date-pickers'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import {
  Typography,
  FormControl,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material'
import {MoonLoader} from 'react-spinners'
import FormWrapper from '../../../../reusable-components/Wrapper/Wrapper'
import {RootState} from '../../../../../setup'
import {useSelector} from 'react-redux'
import FrequencyDateRangePicker from '../../../../reusable-components/NoteType/NoteType'

const selectTimeZone = (state: RootState) => state.timeZone.timeZone

const DeletedNursesNoteForm = () => {
  const params: {id: string} = useParams()
  const [uiElements, setUIElements] = useState([])
  const [isFormLoading, setIsFormLoading] = useState(true)
  const timeZone = useSelector(selectTimeZone)
  const [formData, setFormData] = useState({
    startDate: null,
    endDate: null,
    timeIn: '',
    timeOut: '',
    noteType: '',
  })
  const [clientid, setClientId] = useState<string | null>(null)
  const [clientFullName, setClientFullName] = useState('')
  const [visitType, setVisitType] = useState('')
  const [therapy, setTherapy] = useState('')
  const [isExperiencingPain, setIsExperiencingPain] = useState(false)
  const [painAssessment, setPainAssessment] = useState([
    {location: '', score: '', duration: '', frequency: '', description: '', isExperiencingPain: false},
  ])
  const [hasExperiencedFall, setHasExperiencedFall] = useState(false)
  const [activity, setActivity] = useState([
    {
      hasExperiencedFall: false,
      details: '',
    },
  ])
  const [pocUpdated, setPocUpdated] = useState(false)
  const [poc, setPoc] = useState([
    {
      pocUpdated: false,
      pocDesc: '',
    },
  ])
  const [familyUpdated, setFamilyUpdated] = useState(false)
  const [familyInformed, setFamilyInformed] = useState([
    {
      familyUpdated: false,
      famDesc: '',
    },
  ])

  const buttons = [{label: 'Cancel', variant: 'outlined', type: 'button'}]

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setIsFormLoading(true)

    try {
      const res = await axios.get(`/nursesNotes/getOne/${params.id}?isDeleted=true`)
      const data = res.data.data

      const prevFormData = {...formData}
      const formattedStartDate = dayjs.utc(data.startDate).tz(timeZone, true)
      const formattedEndDate = dayjs.utc(data.endDate).tz(timeZone, true)

      prevFormData['startDate'] = formattedStartDate
      prevFormData['endDate'] = formattedEndDate
      prevFormData['timeIn'] = data?.timeIn
      prevFormData['timeOut'] = data?.timeOut
      prevFormData['noteType'] = data?.noteType
      prevFormData['bp'] = data.vitalSigns?.bp
      prevFormData['pulse'] = data.vitalSigns?.pulse
      prevFormData['rr'] = data.vitalSigns?.rr
      prevFormData['temp'] = data.vitalSigns?.temp
      prevFormData['pain'] = data.vitalSigns?.pain
      prevFormData['o2sat'] = data.vitalSigns?.o2sat
      prevFormData['neuro'] = data.assessment?.neuro
      prevFormData['skin'] = data.assessment?.skin
      prevFormData['heent'] = data.assessment?.heent
      prevFormData['respiratory'] = data.assessment?.respiratory
      prevFormData['cardiac'] = data.assessment?.cardiac
      prevFormData['lastBm'] = data.assessment?.gi?.lastBm
      prevFormData['value'] = data.assessment?.gi?.weight?.value
      prevFormData['type'] = data.assessment?.gi?.weight?.type
      prevFormData['gu'] = data.assessment?.gu
      prevFormData['patientConcerns'] = data.assessment?.patientConcerns
      prevFormData['notes'] = data.assessment?.notes
      prevFormData['dme'] = data.assessment?.dme
      prevFormData['medRec'] = data.assessment?.medRec
      prevFormData['nextAppt'] = data.assessment?.nextAppt
        ? dayjs.utc(data.assessment.nextAppt).tz(timeZone, true)
        : null

      setUIElements(
        EDIT_RECORD_INPUTS.map((item) => ({
          ...item,
          disabled: true,
        }))
      )
      setClientId(data.clientId)
      setClientFullName(data.clientFullName || '')
      setVisitType(data.visitType || '')
      setTherapy(data.therapy || '')
  
      setPainAssessment(data.painAssessment || [])
      if (data.painAssessment && data.painAssessment.length > 0) {
        const hasPainData = data.painAssessment[0].location !== '' || 
                           data.painAssessment[0].score !== '' || 
                           data.painAssessment[0].duration !== '' || 
                           data.painAssessment[0].frequency !== '' || 
                           data.painAssessment[0].description !== ''
        setIsExperiencingPain(hasPainData)
      }      
      setActivity(data.activity || [])
      if (data.activity && data.activity.length > 0) {
        setHasExperiencedFall(data.activity[0].hasExperiencedFall || false)
      }     
      setPoc(data.poc || [])
      if (data.poc && data.poc.length > 0) {
        setPocUpdated(data.poc[0].pocUpdated || false)
      }
      setFamilyInformed(data.familyInformed || [])
      if (data.familyInformed && data.familyInformed.length > 0) {
        setFamilyUpdated(data.familyInformed[0].familyUpdated || false)
      }
      
      setFormData(prevFormData)
    } catch (error) {
      if (error.response) {
        toast.error(`Error: ${error.response.data.message || 'Something went wrong'}`)
      } else if (error.request) {
        toast.error('No response received from the server.')
      } else {
        toast.error(`Error: ${error.message || 'Unknown error occurred'}`)
      }
    } finally {
      setIsFormLoading(false)
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormWrapper
        title='Deleted Nurses Note'
        content={
          <>
            {isFormLoading ? (
              <div
                className='overlay d-flex align-middle justify-content-center align-middle'
                style={{marginTop: '100px'}}
              >
                <MoonLoader color='#9db2fc' size={60} loading={isFormLoading} />
              </div>
            ) : (
              <>
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    marginBottom: '0.5rem',
                    marginLeft: '1rem',
                  }}
                  color='primary'
                >
                  Note Type
                </Typography>
                <FrequencyDateRangePicker
                  isEntry={true}
                  isTemplate={false}
                  isFullForm={false}
                  isEdit={true}
                  formData={formData}
                  setFormData={setFormData}
                  isDeleted={true}
                />
                
                {/* Client Details */}
                <Typography variant="h6" sx={{ ml: 2, mt: 1, mb: 1, color: '#1976d2', fontWeight: 700 }}>
                  Client Details
                </Typography>
                <FormControl
                  sx={{ mb: 2, ml: 1.5, width: '50%', background: 'white' }}
                  color="primary"
                  fullWidth
                  variant="filled"
                  required
                >
                  <TextField value={clientFullName} disabled />
                </FormControl>

                {/* Visit Type */}
                <FormControl
                  sx={{ mb: 2, ml: 1, width: '25%' }}
                  color="primary"
                  fullWidth
                  variant="filled"
                >
                  <TextField
                    value={visitType}
                    sx={{ background: 'white' }}
                    label="VisitType"
                    name="visitType"
                    disabled
                  />
                </FormControl>

                {/* Fall Assessment */}
                <p
                  style={{
                    fontSize: '16px',
                    marginLeft: '15px',
                    color: '#1976d2',
                    fontWeight: 700,
                    fontFamily: 'Roboto',
                  }}
                >
                  Did the patient experience a fall?
                </p>
                <FormControl sx={{ width: '100%', mr: '10px' }}>
                  <RadioGroup
                    row
                    value={hasExperiencedFall ? 'yes' : 'no'}
                    sx={{ mb: 2, ml: 1.5 }}
                  >
                    <FormControlLabel value="yes" control={<Radio disabled />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio disabled />} label="No" />
                  </RadioGroup>

                  {hasExperiencedFall && activity.length > 0 && (
                    <div>
                      {activity.map((fallDetail, index) => (
                        <div key={index} style={{ display: 'flex', justifyContent: 'center' }}>
                          <div style={{ width: '90%' }}>
                            <TextField
                              sx={{ width: '98%', mr: '10px', ml: 1 }}
                              label="Fall details"
                              name="details"
                              value={fallDetail.details}
                              disabled
                              fullWidth
                              margin="normal"
                              multiline
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </FormControl>

                {/* Pain Assessment */}
                <p
                  style={{
                    fontSize: '16px',
                    marginLeft: '15px',
                    color: '#1976d2',
                    fontWeight: 700,
                    fontFamily: 'Roboto',
                  }}
                >
                  Is the patient experiencing Pain?
                </p>
                <RadioGroup
                  row
                  value={isExperiencingPain ? 'yes' : 'no'}
                  sx={{ mb: 2, ml: 1.5 }}
                >
                  <FormControlLabel value="yes" control={<Radio disabled />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio disabled />} label="No" />
                </RadioGroup>
                {isExperiencingPain && painAssessment.length > 0 && (
                  <div>
                    {painAssessment.map((painDetail, index) => (
                      <div key={index} style={{ display: 'flex', justifyContent: 'center' }}>
                        <div style={{ marginBottom: '10px', width: '90%' }}>
                          <TextField
                            sx={{ width: '24%', mr: '10px', ml: 1 }}
                            label="Location"
                            name="location"
                            value={painDetail.location}
                            disabled
                            fullWidth
                            margin="normal"
                          />
                          <TextField
                            type="number"
                            sx={{ width: '24%', mr: '10px', ml: 0.5 }}
                            label="Pain Score"
                            name="score"
                            value={painDetail.score}
                            disabled
                            fullWidth
                            margin="normal"
                          />
                          <TextField
                            sx={{ width: '24%', mr: '8px', ml: 0.5 }}
                            label="Duration"
                            name="duration"
                            value={painDetail.duration}
                            disabled
                            fullWidth
                            margin="normal"
                          />
                          <TextField
                            sx={{ width: '22%', mr: '10px', ml: 1 }}
                            label="Frequency"
                            name="frequency"
                            value={painDetail.frequency}
                            disabled
                            fullWidth
                            margin="normal"
                          />
                          <TextField
                            sx={{ width: index > 0 ? '90%' : '98%', ml: 1 }}
                            label="Description"
                            name="description"
                            value={painDetail.description}
                            disabled
                            fullWidth
                            margin="normal"
                            multiline
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* POC Updated */}
                <p
                  style={{
                    fontSize: '16px',
                    marginLeft: '15px',
                    color: '#1976d2',
                    fontWeight: 700,
                    fontFamily: 'Roboto',
                  }}
                >
                  POC updated?
                </p>
                <FormControl sx={{ width: '100%', mr: '10px', ml: 1 }}>
                  <RadioGroup
                    row
                    value={pocUpdated ? 'yes' : 'no'}
                    sx={{ mb: 2, ml: 1.5 }}
                  >
                    <FormControlLabel value="yes" control={<Radio disabled />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio disabled />} label="No" />
                  </RadioGroup>

                  {pocUpdated && poc.length > 0 && (
                    <div>
                      {poc.map((pocDetail, index) => (
                        <div key={index} style={{ display: 'flex', justifyContent: 'center' }}>
                          <div style={{ width: '90%' }}>
                            <TextField
                              sx={{ width: '98%', mr: '10px', ml: 1 }}
                              label="POC details"
                              name="pocDesc"
                              value={pocDetail.pocDesc}
                              disabled
                              fullWidth
                              margin="normal"
                              multiline
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </FormControl>

                {/* Family Updated */}
                <p
                  style={{
                    fontSize: '16px',
                    marginLeft: '15px',
                    color: '#1976d2',
                    fontWeight: 700,
                    fontFamily: 'Roboto',
                  }}
                >
                  Family updated?
                </p>
                <FormControl sx={{ width: '100%', mr: '10px', ml: 1 }}>
                  <RadioGroup
                    row
                    value={familyUpdated ? 'yes' : 'no'}
                    sx={{ mb: 2, ml: 1.5 }}
                  >
                    <FormControlLabel value="yes" control={<Radio disabled />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio disabled />} label="No" />
                  </RadioGroup>

                  {familyUpdated && familyInformed.length > 0 && (
                    <div>
                      {familyInformed.map((famDetail, index) => (
                        <div key={index} style={{ display: 'flex', justifyContent: 'center' }}>
                          <div style={{ width: '90%' }}>
                            <TextField
                              sx={{ width: '98%', mr: '10px', ml: 1 }}
                              label="Family details"
                              name="famDesc"
                              value={famDetail.famDesc}
                              disabled
                              fullWidth
                              margin="normal"
                              multiline
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </FormControl>

                {/* Therapy */}
                <p
                  style={{
                    fontSize: '16px',
                    marginLeft: '15px',
                    color: '#1976d2',
                    fontWeight: 700,
                    fontFamily: 'Roboto',
                  }}
                >
                  Therapy
                </p>
                <FormControl
                  sx={{ mt: 0, mb: 2, ml: 1.5, mr: 1, width: '32%', border: '1px solid lightgrey' }}
                  color="primary"
                  fullWidth
                  variant="filled"
                >
                  <InputLabel id="note-type-select-label">Select Therapy</InputLabel>
                  <Select
                    labelId="note-type-select-label"
                    value={therapy}
                    sx={{ background: 'white' }}
                    label="Therapy"
                    name="therapy"
                    disabled
                  >
                    <MenuItem value="pt">PT</MenuItem>
                    <MenuItem value="ot">OT</MenuItem>
                    <MenuItem value="st">ST</MenuItem>
                    <MenuItem value="hha">HHA</MenuItem>
                    <MenuItem value="msw">MSW</MenuItem>
                    <MenuItem value="rn">RN</MenuItem>
                    <MenuItem value="cm">CM</MenuItem>
                  </Select>
                </FormControl>

                <DynamicForm
                  fields={uiElements}
                  data={formData}
                  onSubmit={() => {}}
                  buttons={buttons}
                  disabled={true}
                />
              </>
            )}
          </>
        }
      />
    </LocalizationProvider>
  )
}

export default DeletedNursesNoteForm
